import isomorphicFetch from 'isomorphic-fetch'

import { endpoint } from '@library/endpoint'

import routes from 'config/routes'
import { fetch } from 'utils/fetch'

type Headers = {
  'x-forwarded-for'?: string
  'user-agent'?: string
}

type UserTokenInput = {
  email: string
  password: string
  clientId: string | number
  clientSecret: string
  headers: Headers
}

export function getUserToken({
  email,
  password,
  clientId,
  clientSecret,
  headers,
}: UserTokenInput): Promise<{
  data: {
    accessToken: string
    tokenType: string
    expiresIn: number
    refreshToken: string
  }
}> {
  return fetch(
    null,
    `${endpoint('auth')}${routes.auth.userToken}`,
    {
      grant_type: 'password',
      username: email,
      password,
      client_id: clientId,
      client_secret: clientSecret,
    },
    'POST',
    'no-cache',
    {
      'x-forwarded-for': headers['x-forwarded-for'] || '',
      'user-agent': headers['user-agent'] || '',
    },
  )
}

export function getClientToken({
  clientId,
  clientSecret,
  headers,
}: {
  clientId: string | number
  clientSecret: string
  headers: Headers
}): Promise<{ data: { accessToken: string } }> {
  return fetch(
    null,
    `${endpoint('auth')}${routes.auth.clientToken}`,
    { grant_type: 'client_credentials', client_id: clientId, client_secret: clientSecret },
    'POST',
    'no-cache',
    {
      'x-forwarded-for': headers['x-forwarded-for'] || '',
      'user-agent': headers['user-agent'] || '',
    },
  )
}

export function identifyUserUsingOAuth(
  accessToken: string,
): Promise<{ data: { roles: string[]; id: string } }> {
  return fetch(accessToken, `${endpoint('auth')}${routes.auth.identifyUser}`, {}, 'GET')
}

export function refreshUserToken(
  refreshToken: string,
  clientId: string | number,
  clientSecret: string,
): Promise<{
  data: { accessToken: string; token_type: string; expiresIn: number; refreshToken: string }
}> {
  return fetch(
    null,
    `${endpoint('auth')}${routes.auth.refreshToken}`,
    {
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: clientId,
      client_secret: clientSecret,
    },
    'POST',
    'no-cache',
    {},
    null,
  )
}

export function forgetUserToken(accessToken: string): Promise<string> {
  return fetch(accessToken, `${endpoint('auth')}${routes.auth.userToken}`, {}, 'DELETE', 'no-cache')
}

export function validateUserPassword(password: string, version?: number): Promise<string | number> {
  return fetch(
    null,
    `${endpoint('auth', version)}${routes.auth.validateUserPassword}`,
    { password },
    'POST',
    'no-cache',
  )
}

export function resetUserPassword(
  password: string,
  passwordToken: string,
  version = 2,
): Promise<{ data: { email: string } }> {
  return fetch(
    null,
    `${endpoint('auth', version)}${routes.auth.resetUserPassword}`,
    {
      password,
      password_token: passwordToken,
    },
    'POST',
    'no-cache',
  )
}

export function serverAuthenticate(
  email: string,
  password: string,
  rememberMe: boolean,
  recaptchaToken?: string,
  isSignupLogin?: boolean,
  correlationData?: { session_id: string; gousto_ref: string },
) {
  return fetch(
    null,
    `${routes.auth.login}`,
    {
      grant_type: 'password',
      username: email,
      password,
      rememberMe,
      recaptchaToken,
      isSignupLogin,
      correlationData,
    },
    'POST',
    'no-cache',
    {},
    null,
    true,
  )
}

export function serverLogout() {
  return fetch(null, `${routes.auth.logout}`, {}, 'POST', 'no-cache', {}, null, true)
}

export function serverRefresh(rememberMe: boolean) {
  return fetch(null, `${routes.auth.refresh}`, { rememberMe }, 'POST', 'no-cache', {}, null, true)
}

export function identifyUserViaServer(): Promise<{ data: { roles: string[] } }> {
  return fetch(null, `${routes.auth.identify}`, {}, 'POST', 'no-cache', {}, null, true)
}

export function serverForget(accessToken: string) {
  return fetch(null, `${routes.auth.forget}`, { accessToken }, 'POST', 'no-cache', {}, null, true)
}

export function serverValidatePassword(password: string) {
  return fetch(null, `${routes.auth.validate}`, { password }, 'POST', 'no-cache', {}, null, true)
}

export function validateRecaptchaUserToken(
  token: string,
  secret: string,
): Promise<{ success: boolean; 'error-codes'?: string[] }> {
  return new Promise((resolve, reject) => {
    isomorphicFetch(routes.recaptcha.verify, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: `secret=${secret}&response=${token}`,
    })
      .then((response) => response.json())
      .then((data) => {
        resolve(data)
      })
      .catch((error) => {
        reject(error)
      })
  })
}
